import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { getYearOverview } from '../../api/client';

const fmt = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const YearOverview = () => {
  const { COLORS } = useTheme();
  const navigate = useNavigate();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getYearOverview({ year, today: localToday() });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setLoadError(true);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [year]);

  const netColor = (n) => {
    if (n == null) return COLORS.textMuted;
    return n < 0 ? COLORS.red : COLORS.green;
  };

  const summary = data?.summary;
  const periods = data?.periods || [];

  const Tile = ({ label, value, hint }) => (
    <div style={{
      background: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      padding: '14px 12px',
      flex: '1 1 140px',
      minWidth: 0,
    }}>
      <div style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ color: netColor(value), fontFamily: 'monospace', fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
        {fmt(value)}
      </div>
      {hint && (
        <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>{hint}</div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '20px 16px', fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 4 }}>
        <h2 style={{ color: COLORS.text, fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Year Overview</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            style={{
              background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, color: COLORS.text,
              borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 16, fontFamily: 'inherit',
            }}
            aria-label="Previous year"
          >‹</button>
          <span style={{ color: COLORS.text, fontWeight: 800, fontSize: 16, minWidth: 52, textAlign: 'center' }}>{year}</span>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            style={{
              background: COLORS.inputBg, border: `1px solid ${COLORS.border}`, color: COLORS.text,
              borderRadius: 8, width: 34, height: 34, cursor: 'pointer', fontSize: 16, fontFamily: 'inherit',
            }}
            aria-label="Next year"
          >›</button>
        </div>
      </div>
      <p style={{ color: COLORS.textMuted, fontSize: 14, margin: '0 0 16px', lineHeight: 1.5 }}>
        How the year is going so far, and where it lands if you keep this pace — versus if you hit the budget from here.
      </p>

      {loading ? (
        <p style={{ color: COLORS.textMuted }}>Loading…</p>
      ) : loadError ? (
        <p style={{ color: COLORS.red }}>Couldn't load year overview. Try again in a moment.</p>
      ) : !data ? (
        <p style={{ color: COLORS.textMuted }}>No data yet.</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
            <Tile
              label="So far"
              value={summary.ytdActualNet}
              hint={summary.closedCount + (periods.some((p) => p.status === 'current') ? 1 : 0) > 0
                ? `Actual net across finished months${periods.some((p) => p.status === 'current') ? ' + this period' : ''}`
                : 'No periods with actuals yet'}
            />
            <Tile
              label="At this pace"
              value={summary.paceEndNet}
              hint={summary.futureCount > 0
                ? `EOY if remaining ${summary.futureCount} period${summary.futureCount === 1 ? '' : 's'} match your ~${fmt(summary.avgClosedActualNet)}/period pace`
                : 'Year already finished — same as so far'}
            />
            <Tile
              label="On budget"
              value={summary.budgetEndNet}
              hint="EOY if finished months stay locked and current + remaining hit plan"
            />
          </div>
          <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '0 0 16px', lineHeight: 1.45 }}>
            Planned YTD net: {fmt(summary.ytdPlannedNet)}.
            {summary.ytdActualNet != null && summary.ytdPlannedNet != null && (
              <> Diff so far: <span style={{ color: netColor(summary.ytdActualNet - summary.ytdPlannedNet), fontFamily: 'monospace', fontWeight: 700 }}>
                {fmt(summary.ytdActualNet - summary.ytdPlannedNet)}
              </span> (positive = ahead of plan).</>
            )}
          </p>

          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 8,
              padding: '10px 14px', borderBottom: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
            }}>
              <span>Period</span>
              <span style={{ textAlign: 'right' }}>Actual</span>
              <span style={{ textAlign: 'right' }}>Planned</span>
            </div>
            {periods.map((p) => {
              const clickable = p.status !== 'future';
              const isCurrent = p.status === 'current';
              const RowTag = clickable ? 'button' : 'div';
              return (
                <RowTag
                  key={p.key}
                  type={clickable ? 'button' : undefined}
                  onClick={clickable ? () => navigate(`/analytics/monthly?month=${p.key}`) : undefined}
                  style={{
                    width: '100%', textAlign: 'left',
                    display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 8, alignItems: 'center',
                    padding: '12px 14px',
                    border: 'none',
                    borderBottom: `1px solid ${COLORS.border}`,
                    borderLeft: isCurrent ? `3px solid ${COLORS.accent}` : '3px solid transparent',
                    background: isCurrent ? COLORS.inputBg : 'transparent',
                    cursor: clickable ? 'pointer' : 'default',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ color: COLORS.text, fontSize: 13, fontWeight: isCurrent ? 700 : 600 }}>
                    {p.label}
                    {isCurrent && (
                      <span style={{ color: COLORS.accent, fontSize: 11, fontWeight: 700 }}> · so far</span>
                    )}
                    {p.status === 'future' && (
                      <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 600 }}> · planned</span>
                    )}
                  </span>
                  <span style={{
                    textAlign: 'right', fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
                    color: p.actualNet == null ? COLORS.textMuted : netColor(p.actualNet),
                  }}>
                    {fmt(p.actualNet)}
                  </span>
                  <span style={{
                    textAlign: 'right', fontFamily: 'monospace', fontSize: 13, fontWeight: 600,
                    color: COLORS.textMuted,
                  }}>
                    {fmt(p.plannedNet)}
                  </span>
                </RowTag>
              );
            })}
            <div style={{
              display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 8,
              padding: '12px 14px', background: COLORS.inputBg,
            }}>
              <span style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                Year net
              </span>
              <span style={{
                textAlign: 'right', fontFamily: 'monospace', fontSize: 14, fontWeight: 800,
                color: netColor(summary.paceEndNet),
              }} title="At this pace">
                {fmt(summary.paceEndNet)}
              </span>
              <span style={{
                textAlign: 'right', fontFamily: 'monospace', fontSize: 14, fontWeight: 800,
                color: netColor(summary.budgetEndNet),
              }} title="On budget">
                {fmt(summary.budgetEndNet)}
              </span>
            </div>
          </div>
          <p style={{ color: COLORS.textMuted, fontSize: 11, margin: '8px 2px 0' }}>
            Footer: left = at this pace · right = on budget. Tap a past or current period for the Monthly Deep Dive.
          </p>
        </>
      )}
    </div>
  );
};

export default YearOverview;
